import React, {
  Dispatch, SetStateAction, useEffect, useState,
} from 'react';
import {
  View, StyleSheet, TextInput, Platform, Text, Pressable, FlatList,
} from 'react-native';
import Colors from '../../../../../constants/Colors';
import { CITY_EMPTY_ERROR } from './errorMessages';
import Style from '../../../../../constants/Style';
import CloseIconSmall from '../../../../../components/12pxIcons/CloseIconSmall';
import titleCase from '../../../../../components/TitleCase';
import SearchIcon from '../../../../../components/SearchIcon';

export type City = {
  countryCode: string,
  stateCode: string,
  name: string,
}

interface CitySelectorProps {
  selectedCity: City,
  setSelectedCity: Dispatch<SetStateAction<City>>,
  cities: City[],
  cityInvalid: boolean,
  cityDropdownShown: boolean,
  setCityDropdownShown: Dispatch<SetStateAction<boolean>>,
}

const CitySelector = ({
  selectedCity, setSelectedCity, cities, cityInvalid, cityDropdownShown, setCityDropdownShown,
}: CitySelectorProps) => {
  const [value, onChangeText] = useState<string>('');

  useEffect(() => {
    if (!selectedCity) setCityDropdownShown(true);
  }, [value]);

  return <View style={styles.container}>
    <TextInput
      maxLength={50}
      {...{ value, onChangeText }}
      style={[styles.text, styles.input, cityInvalid && styles.invalid]}
      placeholder='Enter'
      placeholderTextColor={styles.text.color}
      editable={!selectedCity}
      onFocus={() => setCityDropdownShown(true)}
      testID='lm-city-input-field'
    />
    {selectedCity ? <View style={styles.selectedItem}>
      <Text style={styles.text}>{selectedCity?.name}</Text>
      {selectedCity && <Pressable onPress={() => setSelectedCity(null)}>
        <CloseIconSmall style={styles.closeIcon} />
      </Pressable>}
    </View> : <>
      {(cityDropdownShown && value?.length > 0) ? <FlatList
        style={styles.dropdownView}
        data={cities?.filter((city: City) => city.name.toLowerCase().includes(value.toLowerCase()))}
        keyExtractor={(item: City) => `city_option_${item.countryCode}_${item.stateCode}_${item.name}`}
        testID='city-list'
        renderItem={({ item }) => <Pressable
          onPress={() => {
            setSelectedCity(item);
            setCityDropdownShown(false);
          }}
          style={(state: { hovered: boolean, pressed: boolean }) => ([
            state.hovered && Style.listItemHovered, state.pressed && Style.listItemPressed,
          ])}
        >
          <Text
            style={[
              styles.text, styles.option,
              selectedCity?.countryCode === item.countryCode
              && selectedCity?.stateCode === item.stateCode && selectedCity?.name === item.name
              && styles.optionSelected,
            ]}
            selectable={false}
          >
            {titleCase(item?.name)}
          </Text>
        </Pressable>}
      /> : (cityInvalid && <Text style={styles.errorText}>{CITY_EMPTY_ERROR}</Text>)}
      <SearchIcon style={styles.searchIcon} />
    </>}
  </View>;
};

const styles = StyleSheet.create({
  container: {
    alignSelf: 'stretch',
    marginTop: 4,
    zIndex: 1,
  },
  searchIcon: {
    position: 'absolute',
    end: 9,
    top: 10,
  },
  text: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    alignItems: 'center',
  },
  input: {
    padding: 10,
    lineHeight: Platform.OS === 'ios' ? undefined : 24,
    borderRadius: 5,
    backgroundColor: Colors.light.hereworksWhite200,
    alignSelf: 'stretch',
  },
  emoji: {
    fontSize: 28,
    lineHeight: 42,
    marginStart: 4,
  },
  selectedItem: {
    borderColor: Colors.light.hereworksBlue200,
    backgroundColor: Colors.light.hereworksBlue100,
    borderWidth: 1,
    borderRadius: 4,
    paddingHorizontal: 8,
    position: 'absolute',
    bottom: 2,
    start: 6,
    flexDirection: 'row',
    alignItems: 'center',
    height: '90%',
  },
  closeIcon: {
    marginStart: 6,
    marginTop: 2,
  },
  dropdownView: {
    top: 52,
    maxHeight: 180,
    width: 170,
    borderWidth: 1,
    borderRadius: 6,
    backgroundColor: '#FFF',
    position: 'absolute',
    borderColor: Colors.light.hereworksBlue100,
  },
  option: {
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  optionSelected: {
    backgroundColor: Colors.light.hereworksDarkBlue,
    color: '#FFF',
  },
  invalid: {
    borderColor: Colors.light.hereworksRed500,
    borderWidth: 1,
  },
  errorText: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksRed500,
  },
});

export default CitySelector;
